// frontend/src/pages/reporting/reporting/ProfileDirectoryPage.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { Card, SearchInput, Pagination, PractitionerCard } from "@common";
import Filter from "@components/common/Filter";
import { PractitionerQuickLookModal } from "@common";
import CreatePractitionerSection from "./CreatePractitionerSection";
import {
  useUpdatePractitionerByClinic,
  useSearchPractitioners,
  usePractitioners,
} from "@api/hooks";

import { safeText, normalizeTypeForUi, pickBadgeColor } from "@utils/utils";
import { getRoster, matchesFilter, sortNewestFirst } from "@utils/practitionerUtils";
import { TYPE_LABELS, TYPE_ORDER } from "@utils/practitionerTypes";
import {
  FORM_IDS,
  canAccessPractitionerDirectory,
  canEditPractitionerDirectory,
  isManagerOrAbove,
  isPractitionerLead,
} from "@utils";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";

// Per-type card background colours. Each value uses the *-50 (very light) shade for the
// background and the *-100 shade for the border, giving each role a subtle tint without
// overwhelming the card content. Colours mirror TYPE_COLORS in practitionerTypes.js but
// at a lower saturation so they read as backgrounds rather than badges.
//
// IMPORTANT: Tailwind JIT only includes classes that appear as complete static strings in
// source files. Never build these dynamically (e.g. `bg-${color}-50`) — the purger will
// strip them and the colour will not appear in the compiled bundle.
const CARD_TINT = {
  DDS: "bg-palette-blue-soft border-palette-blue-muted",
  HYG: "bg-palette-emerald-soft border-palette-emerald-muted",
  PL: "bg-palette-orange-soft border-palette-orange-muted",
  AM: "bg-palette-amber-soft border-palette-amber-muted",
  RCP: "bg-palette-sky-soft border-palette-sky-muted",
  RC: "bg-palette-purple-soft border-palette-purple-muted",
  TC: "bg-palette-rose-soft border-palette-rose-muted",
  DAL1: "bg-palette-yellow-soft border-palette-yellow-muted",
  DAL2: "bg-palette-fuchsia-soft border-palette-fuchsia-muted",
  HYGR: "bg-palette-cyan-soft border-palette-cyan-muted",
  NUR: "bg-palette-pink-soft border-palette-pink-muted",
  RN: "bg-palette-violet-soft border-palette-violet-muted",
  FT: "bg-palette-stone-soft border-palette-stone-muted",
  STR: "bg-palette-teal-soft border-palette-teal-muted",
  SRG: "bg-palette-red-soft border-palette-red-muted",
  SPC: "bg-palette-lime-soft border-palette-lime-muted",
};

// Falls back to plain white for any unrecognised type code.
function cardTintClass(typeUi) {
  return CARD_TINT[typeUi] ?? "bg-white border-palette-gray-divider";
}

export default function ProfileDirectoryPage(props) {
  const clinicId = props.clinicId;
  const role = props.role; // from parent (e.g. ClinicReport user.role) — drives all flags below

  const isManagerOrAboveRole = isManagerOrAbove(role);
  const isPractitionerLeadRole = isPractitionerLead(role);

  /*
   * Practitioner Lead (DEN72):
   * - View directory: yes (canAccessPractitionerDirectory in @utils/roles).
   * - Create new practitioner: yes, personal/basic fields only (create modal gets
   *   hideFinancialFields, matching the edit modal). Backend allows the POST for
   *   role 4 within its own clinic (PractitionerViewSet.create).
   * - Edit in modal: yes, but hideFinancialFields → personal PATCH only (see modal + buildPractitionerPersonalUpdatePayload).
   */
  const canAccessDirectory = canAccessPractitionerDirectory(role);
  // View-only roles (Observer, Bookkeeper) cannot create or edit
  const canCreatePractitioner = isManagerOrAboveRole || isPractitionerLeadRole;
  const canEditPractitioner = canEditPractitionerDirectory(role); // false for Observer/Bookkeeper

  const [filter, setFilter] = useState("ALL");
  const [q, setQ] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Global API search state
  const [globalSearchQ, setGlobalSearchQ] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState(null); // null = not searched yet
  const searchMut = useSearchPractitioners();
  const searching = searchMut.isPending;
  const globalSearchInputRef = useRef(null);

  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(
    FORM_IDS.PROFILE_DIRECTORY_SEARCH,
  );

  const [quickLookOpen, setQuickLookOpen] = useState(false);
  const [activePractitioner, setActivePractitioner] = useState(null);

  // ✅ NEW: tells modal whether to open directly in edit mode
  const [openInEdit, setOpenInEdit] = useState(false);

  const updateMut = useUpdatePractitionerByClinic();

  // ── Server-side pagination (opt-in via `serverPaginated`) ────────────────────
  // Used by the All-Clinics directory so the API returns 12 records per request
  // (?limit=12&offset=N) instead of all 516 at once. When this prop is NOT set
  // (e.g. ClinicReport's single-clinic view), the component keeps its original
  // client-side behaviour and reads from props.practitionersData below.
  const serverPaginated = !!props.serverPaginated;

  // Debounce the inline "Search name…" box so server mode fires one request after
  // typing stops, not one per keystroke.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(safeText(q).trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Any change to the filter or search must reset to page 1, otherwise you could be
  // stranded on a page number that no longer exists in the smaller result set.
  useEffect(() => {
    if (serverPaginated) setCurrentPage(1);
  }, [serverPaginated, filter, debouncedQ, clinicId]);

  const serverQuery = {
    ...(clinicId ? { clinic: clinicId } : {}),
    limit: itemsPerPage,
    offset: (Math.max(1, currentPage) - 1) * itemsPerPage,
    // Keep the roster grouped by clinical role (Dentists, then Hygienists, …) across pages.
    order_by: "type",
    // The dropdown value is the UI code ("DDS"); the backend stores the lowercase code ("dds").
    ...(filter !== "ALL" ? { practitioner_type: filter.toLowerCase() } : {}),
    ...(debouncedQ ? { search: debouncedQ } : {}),
  };
  const { data: serverData, isFetching: serverFetching } = usePractitioners(serverQuery, {
    enabled: serverPaginated,
    placeholderData: keepPreviousData, // v5: keep showing the current page while the next loads
  });

  // Single source of truth for the rest of the component, regardless of mode.
  const practitionersSource = serverPaginated ? serverData : props.practitionersData;
  const loading = serverPaginated ? serverFetching : props.isLoading;
  // Server mode: the backend already returned the exact page + the true grand total.
  const serverTotal = serverPaginated ? (serverData?.count ?? 0) : 0;

  const rosterSorted = useMemo(() => {
    const roster = getRoster(practitionersSource);

    const cleaned = [];
    for (let i = 0; i < roster.length; i++) {
      const p = roster[i];
      const name = safeText(p?.name);
      if (name) cleaned.push(p);
    }

    // Server mode already returns rows ordered (-created_at, -id) and filtered, so keep
    // that order untouched; client mode sorts locally as before.
    return serverPaginated ? cleaned : sortNewestFirst(cleaned);
  }, [practitionersSource, serverPaginated]);

  // ✅ filtered + grouped DDS first, HYG second, inactive last
  const filtered = useMemo(() => {
    // Server mode: filtering, search and ordering all happen on the backend, and the
    // rows we have ARE the current page — render them as-is (no client slicing/sorting).
    if (serverPaginated) return rosterSorted;

    const query = safeText(q).toLowerCase();

    const matches = [];

    for (let i = 0; i < rosterSorted.length; i++) {
      const p = rosterSorted[i];
      const name = safeText(p?.name);
      const typeUi = normalizeTypeForUi(p?.practitioner_type);

      if (!matchesFilter(typeUi, filter)) continue;

      if (query) {
        const hay = (name + " " + typeUi).toLowerCase();
        if (!hay.includes(query)) continue;
      }

      matches.push(p);
    }

    // Sort active practitioners by TYPE_ORDER (clinical roles first, support last),
    // then push inactive records to the bottom regardless of type.
    // TYPE_ORDER is already a {type: index} object so it doubles as the sort map.
    const typeOrderMap = TYPE_ORDER;
    matches.sort((a, b) => {
      const aInactive = safeText(a?.status).toLowerCase() === "inactive" ? 1 : 0;
      const bInactive = safeText(b?.status).toLowerCase() === "inactive" ? 1 : 0;
      if (aInactive !== bInactive) return aInactive - bInactive;
      const aType = typeOrderMap[normalizeTypeForUi(a?.practitioner_type)] ?? 99;
      const bType = typeOrderMap[normalizeTypeForUi(b?.practitioner_type)] ?? 99;
      return aType - bType;
    });

    return matches;
  }, [rosterSorted, filter, q, serverPaginated]);

  // Counts every type across the full unfiltered roster so the header stats
  // always reflect the clinic total, not just what the current filter shows.
  const typeCounts = useMemo(() => {
    const counts = {};
    for (let i = 0; i < rosterSorted.length; i++) {
      const t = normalizeTypeForUi(rosterSorted[i]?.practitioner_type);
      if (t) counts[t] = (counts[t] ?? 0) + 1;
    }
    return counts;
  }, [rosterSorted]);

  // Grand total of matching records: server mode uses the API's `count` (all pages);
  // client mode uses the length of the locally-filtered list.
  const paginationTotal = serverPaginated ? serverTotal : filtered.length;

  const totalPages = useMemo(() => {
    const per = itemsPerPage || 10;
    return Math.max(1, Math.ceil(paginationTotal / per));
  }, [paginationTotal, itemsPerPage]);

  useEffect(() => {
    setCurrentPage((p) => {
      if (p > totalPages) return totalPages;
      if (p < 1) return 1;
      return p;
    });
  }, [totalPages]);

  const pageSlice = useMemo(() => {
    // Server mode: `filtered` already IS the current page from the API — don't slice again.
    if (serverPaginated) return filtered;
    const per = itemsPerPage || 10;
    const page = currentPage || 1;
    const start = (page - 1) * per;
    return filtered.slice(start, start + per);
  }, [serverPaginated, filtered, currentPage, itemsPerPage]);

  // ✅ Unified modal opener
  function openModal(p, startEdit = false) {
    setActivePractitioner(p);
    setOpenInEdit(!!startEdit);
    setQuickLookOpen(true);
  }

  // ✅ Open Practitioner Profile tab (ClinicReport) or fall back to quick-look modal (Admin/Management home)
  function openProfileView(p) {
    if (props.onViewPractitioner) {
      props.onViewPractitioner(p);
      return;
    }
    openModal(p, false);
  }

  async function handleGlobalSearch(e) {
    e?.preventDefault();
    const term = globalSearchQ.trim();
    if (!term) return;
    try {
      const data = await searchMut.mutateAsync({ search: term });
      const results = Array.isArray(data) ? data : (data?.results ?? []);
      setGlobalSearchResults(results);
      recordSubmitSuccess();
    } catch {
      setGlobalSearchResults([]);
    }
  }

  async function handleUpdatePractitioner(original, payload) {
    // For PL, payload is only personal fields (modal); managers send full buildUpdatePayload.
    if (!canEditPractitioner) throw new Error("No permission");
    // When the directory is in "All Clinics" mode (clinicId === null), fall back
    // to the practitioner's own clinic so updates still target the correct row.
    const effectiveClinicId = clinicId || original?.clinic;
    if (!effectiveClinicId) throw new Error("Missing clinic id");

    await updateMut.mutateAsync({
      clinicCode: effectiveClinicId,
      id: original.id,
      data: payload,
      method: "patch",
    });
  }

  // Visitors etc. — not PL, not manager: no directory at all
  if (!canAccessDirectory) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4">
        <Card className="mt-4">
          <div className="text-sm text-palette-red-badge font-semibold">
            You do not have access to view this page.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4">
      <Card className="mt-4">
        {/* PL can create, but without the compensation/insurance inputs */}
        <CreatePractitionerSection
          clinicId={clinicId}
          canCreate={canCreatePractitioner}
          hideFinancialFields={isPractitionerLeadRole}
        />

        <PractitionerQuickLookModal
          isOpen={quickLookOpen}
          onClose={() => {
            setQuickLookOpen(false);
            setActivePractitioner(null);
            setOpenInEdit(false);
          }}
          practitioner={activePractitioner}
          canEdit={canEditPractitioner} // PL true — may save, but only non-financial fields shown
          hideFinancialFields={isPractitionerLeadRole} // PL true — hides pay/insurance/grid; slim PATCH on save
          onSave={canEditPractitioner ? handleUpdatePractitioner : null}
          openInEdit={openInEdit} // ✅ NEW
          allowAllTypes // Employee Profile is the one place all practitioner types are selectable
        />

        {/* Global Practitioner Search */}
        <div className="mb-6 rounded-xl border border-palette-gray-divider bg-palette-gray-soft p-4">
          <div className="text-sm font-semibold text-palette-slate-heading mb-2">
            Search Practitioners
          </div>
          <form
            onSubmit={handleGlobalSearch}
            className="flex gap-2 items-center"
            {...formTrackingProps}
          >
            <input
              ref={globalSearchInputRef}
              type="text"
              value={globalSearchQ}
              onChange={(e) => {
                setGlobalSearchQ(e.target.value);
                if (!e.target.value.trim()) setGlobalSearchResults(null);
              }}
              placeholder="Search by name, email..."
              className="flex-1 rounded-xl border border-palette-gray-divider px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-palette-indigo-label bg-white"
            />
            <button
              type="submit"
              disabled={searching || !globalSearchQ.trim()}
              className="px-4 py-2 rounded-xl bg-palette-indigo-badge text-white text-sm font-medium hover:bg-palette-indigo-heading disabled:opacity-50 transition"
            >
              {searching ? "Searching..." : "Search"}
            </button>
            {globalSearchResults !== null && (
              <button
                type="button"
                onClick={() => {
                  setGlobalSearchResults(null);
                  setGlobalSearchQ("");
                }}
                className="px-3 py-2 rounded-xl border border-palette-gray-divider text-sm text-palette-gray-secondary hover:bg-palette-gray-subtle transition"
              >
                Clear
              </button>
            )}
          </form>

          {/* Search Results */}
          {globalSearchResults !== null && (
            <div className="mt-3">
              {globalSearchResults.length === 0 ? (
                <div className="text-sm text-palette-gray-secondary">No practitioners found.</div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs text-palette-gray-placeholder mb-1">
                    {globalSearchResults.length} result{globalSearchResults.length !== 1 ? "s" : ""}
                  </div>
                  {globalSearchResults.map((p) => {
                    const typeUi = normalizeTypeForUi(p?.practitioner_type);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => openModal(p, false)}
                        className="w-full text-left flex items-center justify-between gap-3 rounded-lg border border-palette-gray-divider bg-white px-4 py-3 hover:bg-palette-indigo-soft hover:border-palette-indigo-border transition"
                      >
                        <div>
                          <div className="text-sm font-medium text-palette-slate-primary">
                            {safeText(p?.name) || "—"}
                          </div>
                          <div className="text-xs text-palette-gray-secondary">
                            {p?.clinic && <span className="mr-2">{p.clinic}</span>}
                            <span className="uppercase">{typeUi}</span>
                          </div>
                        </div>
                        <span className="text-xs text-palette-indigo-badge shrink-0">View →</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Header */}
        <div className="mb-4">
          <div className="text-sm font-semibold text-palette-slate-badge">Directory</div>

          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-lg font-bold text-palette-slate-primary leading-tight">
                {clinicId ? clinicId : "Clinic"} — Profiles
              </div>
            </div>

            <div className="text-xs text-palette-gray-secondary">
              <div className="flex flex-wrap gap-x-3 gap-y-1 justify-end">
                <span className="font-semibold text-palette-slate-heading">
                  Total:{" "}
                  {loading && rosterSorted.length === 0
                    ? "…"
                    : serverPaginated
                      ? serverTotal
                      : rosterSorted.length}
                </span>
                {/* Per-type counts come from the full local roster, so only show them in
                    client mode. In server mode we only hold the current page, so a per-type
                    breakdown would be misleading — the grand Total above is accurate. */}
                {!serverPaginated &&
                  Object.keys(TYPE_ORDER)
                    .filter((t) => typeCounts[t])
                    .map((t) => (
                      <span key={t}>
                        {t}:{" "}
                        <span className="font-medium text-palette-slate-badge">
                          {typeCounts[t]}
                        </span>
                      </span>
                    ))}
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-start sm:gap-4">
          {/* w-56 overrides the Filter default (130 px) — needed because labels like
              "Dental Assistant L1 Only" are too long for the narrow default width. */}
          <Filter
            label="Type"
            value={filter}
            className="w-full sm:w-56"
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: "ALL", label: "All" },
              // TYPE_ORDER is a {code: index} object, not an array — use Object.keys to iterate.
              // Insertion order matches the clinical priority order defined in practitionerTypes.js.
              ...Object.keys(TYPE_ORDER).map((code) => ({
                value: code,
                label: `${TYPE_LABELS[code]} Only`,
              })),
            ]}
          />

          <SearchInput
            value={q}
            onChange={(val) => {
              setQ(val);
              setCurrentPage(1);
            }}
            placeholder="Search name..."
            className="w-full sm:w-auto"
          />
        </div>

        {/* ✅ Grid */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {loading && pageSlice.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-palette-gray-secondary">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-palette-indigo-label border-t-transparent" />
              Loading practitioners…
            </div>
          ) : pageSlice.length === 0 ? (
            <div className="text-sm text-palette-gray-secondary">
              No practitioners found for this filter/search.
            </div>
          ) : (
            pageSlice.map((p) => {
              const typeUi = normalizeTypeForUi(p?.practitioner_type);
              const isInactive = safeText(p?.status).toLowerCase() === "inactive";

              return (
                <div
                  key={p.id}
                  className={[
                    "text-left w-full",
                    "rounded-2xl border p-5",
                    "transition hover:shadow-sm",
                    isInactive
                      ? "bg-palette-gray-subtle border-palette-gray-divider opacity-60 blur-[0.5px]"
                      : cardTintClass(typeUi),
                  ].join(" ")}
                >
                  <PractitionerCard
                    practitioner={p}
                    typeUi={typeUi}
                    status={safeText(p?.status)}
                    badgeColor={pickBadgeColor(typeUi)}
                    actionLabel={canEditPractitioner ? "Edit" : "View"} // PL: “Edit” opens restricted modal
                    onAction={() => openModal(p, canEditPractitioner)} // second arg: open in edit for anyone who can edit
                    viewLabel="View"
                    onView={() => openProfileView(p)}
                  />
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {paginationTotal > 0 && (
          <div className="mt-6 border-t border-palette-gray-subtle pt-4">
            <Pagination
              page={currentPage}
              pageSize={itemsPerPage}
              total={paginationTotal}
              onPageChange={(next) => setCurrentPage(next)}
              onPageSizeChange={(nextSize) => {
                setItemsPerPage(nextSize);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
